
import React, { useMemo } from 'react';
import PlanCard from './PlanCard';

const PlanGrid = ({ plans, onPlanSelect, loading, error }) => {
  const validPlans = useMemo(() => {
    if (!plans || plans.length === 0) return [];
    return plans.filter((plan) => plan && plan.name && plan.planCode);
  }, [plans]);

  // Loading
  if (loading) {
    return (
      <div className="min-w-0">
        <p className="text-xs text-gray-500 mb-3 text-center sm:text-left">
          Choose a subscription plan (optional). You can always just pay per video.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm animate-pulse"
            >
              <div className="h-6 bg-gray-200 rounded mb-3" />
              <div className="h-4 bg-gray-200 rounded mb-2" />
              <div className="h-8 bg-gray-200 rounded mb-4" />
              <div className="space-y-2">
                <div className="h-3 bg-gray-200 rounded" />
                <div className="h-3 bg-gray-200 rounded" />
                <div className="h-3 bg-gray-200 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 text-4xl mb-4">⚠️</div>
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Failed to load plans</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Empty
  if (!validPlans || validPlans.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 text-4xl mb-4">📋</div>
        <h3 className="text-xl font-semibold text-gray-800 mb-2">No plans available</h3>
        <p className="text-gray-600">Please check back later or contact support.</p>
      </div>
    );
  }

  return (
    <div className="min-w-0">
      {/* Helper copy to clarify subscriptions vs pay-per-video */}
      <p className="text-xs text-gray-500 mb-3 text-center sm:text-left">
        Subscriptions are optional and give you storage plus free monthly video credits.
        You can still pay per video without a subscription.
      </p>

      {/* 
        Layout:
        - Mobile: single column (vertical scroll, one card per row)
        - Small / medium: 2 columns
        - Large+: 4 columns so all plans align horizontally with minimal scrolling
      */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        {validPlans.map((plan) => (
          <div key={plan.planCode} className="min-w-0">
            <PlanCard
              plan={plan}
              onSelect={() => onPlanSelect(plan)}
              isPopular={plan.name === 'Elite'}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlanGrid;
